/* eslint-disable camelcase */
import fetch from '../../api/fetch';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { parseError } from '../../utils/fetchErrorParser';
import store from '../store';
import { enqueueSnackbar } from './snackbar';
import { modifyMetricData, modifyMetricDataByKey } from '../../utils/getMetrics';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

export const addNewHydraulicsRow = (type) => (dispatch) => {
  const { hydraulics } = store.getState();
  const newHydraulics = [...hydraulics.data];
  const existUnit = { ...hydraulics.units };
  const nextId = newHydraulics.length - 1;
  const newRow = {
    name: '',
    details: [],
    grid_id: nextId + 1,
    _id: generateRandom(),
    isNewRow: true,
    isDirty: true,
    notes: '',
    type,
    index: newHydraulics.length
  };
  newHydraulics.unshift(newRow);
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_SUCCESS,
    payload: {
      data: newHydraulics,
      unModifiedData: newHydraulics,
      units: existUnit,
      paginatedElements: newHydraulics.length,
    },
  });
};

export const addNewHydraulicsDetailsRow = (unit) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
  });
  const { hydraulics } = store.getState();
  const newHydraulicsDetails = [...hydraulics.details.data];
  const existUnit = { ...hydraulics?.details?.units };
  const nextId = newHydraulicsDetails.length - 1;
  const newRow = {
    rheo_model: 'BEST FIT',
    hole_depth: '',
    bit_depth: '',
    bit_depth_offset: 0.0,
    correction_factor: 1.0,
    rop: 0,
    sbp: 0,
    rpm: 0,
    flow_in: 0,
    drillStringsId: '',
    drillFluidsId: '',
    cuttings_data: unit === UNIT_SYSTEM.US
      ? {
          diameter: 0.025,
          thickness: 0.025,
          density: 21.75,
          porosity: 0.22
        }
      : {
          diameter: 0.6,
          thickness: 0.6,
          density: 2606,
          porosity: 0.22
        },
    target_data: {},
    grid_id: nextId + 1,
    _id: generateRandom(),
    isNewRow: true,
    isDirty: true,
  };
  newHydraulicsDetails.unshift(newRow);
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SUCCESS,
    payload: {
      data: newHydraulicsDetails,
      unModifiedData: newHydraulicsDetails,
      units: existUnit,
      paginatedElements: newHydraulicsDetails.length,
    },
  });
};

export const listHydraulics = (query, closeDialog) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  const body = {
    query
  };
  return fetch
    .post('hydraulicsRuns/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: hydraulicsData, units } = data;
      const holeDepthUnit = units?.results?.depths_summary?.hole_depth;
      const listData = hydraulicsData.map((item) => {
        const modifiedRunDetailsCopy = modifyMetricData(
          [item.run_details_copy],
          units?.inputs,
          currentUnit
        )[0];
        const modifiedRunDetails = modifyMetricData(
          [item.run_details],
          units?.inputs,
          currentUnit
        )[0];

        const {
          summary,
          depth_points,
          pump_ramps,
          pwd,
          depths_summary
        } = item.results ?? item.run_response ?? {};

        const { casing_shoe, hole_depth, landing_point, ...otherDepthSummary } = depths_summary || {};
        const secondaryFieldsSet = new Set();
        const modifiedCasingShoe = modifyMetricData([casing_shoe], units?.results?.depths_summary?.casing_shoe, currentUnit)[0];
        const modifiedHoleDepth = modifyMetricData([hole_depth], units?.results?.depths_summary?.hole_depth, currentUnit)[0];
        const modifiedLandingPoint = modifyMetricData([landing_point], units?.results?.depths_summary?.landing_point, currentUnit)[0];

        // Dynamically modify other keys
        const modifiedOtherDepths = {};
        for (const [key, value] of Object.entries(otherDepthSummary)) {
          const modifiedValue = modifyMetricData([value], units?.results?.depths_summary?.hole_depth, currentUnit)[0];
          modifiedOtherDepths[key] = modifiedValue;
          secondaryFieldsSet.add(key);
          if (!units.results?.depths_summary[key]) {
            units.results.depths_summary[key] = holeDepthUnit;
          }
        }

        const { cuttings_data, target_data } = item.run_details || {};
        const { cuttings_data: cuttingData, target_data: targetData } = item.run_details_copy || {};
        const isRunDetailsEmpty = Object.keys(item.run_details || {}).length === 0;
        const isRunDetailsCopyEmpty = Object.keys(item.run_details_copy || {}).length === 0;

        const modifiedSummary = modifyMetricData(
          [summary],
          units?.results?.summary,
          currentUnit
        )[0];

        const modifiedDepthPoints = modifyMetricData(
          depth_points,
          units?.results?.depth_points,
          currentUnit
        );

        const modifiedCuttingData = modifyMetricData(
          [cuttings_data],
          units?.inputs?.cuttings_data,
          currentUnit
        )[0];

        const modifiedCuttingData_RunDetailsCopy = modifyMetricData(
          [cuttingData],
          units?.inputs?.cuttings_data,
          currentUnit
        )[0];

        const modifiedTargetData = modifyMetricData(
          [target_data],
          units?.inputs?.target_data,
          currentUnit
        )[0];

        const modifiedTargetData_RunDetailsCopy = modifyMetricData(
          [targetData],
          units?.inputs?.target_data,
          currentUnit
        )[0];

        const modifiedPumpRamps = modifyMetricData(
          pump_ramps,
          units?.results?.pump_ramps,
          currentUnit
        );

        const modifiedPWD =
          Array.isArray(pwd) && pwd.length > 0
            ? modifyMetricData(pwd, units?.results?.pwd, currentUnit)
            : pwd;

        return {
          ...item,
          type: item.type || 'hydraulicsRun',
          run_details: isRunDetailsEmpty
            ? {}
            : {
                ...modifiedRunDetails,
                cuttings_data: modifiedCuttingData,
                target_data: modifiedTargetData,
              },
          run_details_copy: isRunDetailsCopyEmpty
            ? {}
            : {
                ...modifiedRunDetailsCopy,
                cuttings_data: modifiedCuttingData_RunDetailsCopy,
                target_data: modifiedTargetData_RunDetailsCopy,
              },
          results: {
            ...(item.results ?? item.run_response ?? {}),
            pump_ramps: modifiedPumpRamps,
            summary: modifiedSummary,
            depth_points: modifiedDepthPoints,
            pwd: modifiedPWD,
            depths_summary: {
              casing_shoe: modifiedCasingShoe,
              hole_depth: modifiedHoleDepth,
              landing_point: modifiedLandingPoint,
              ...modifiedOtherDepths
            }
          },
          isHydraulicsRunResults: !!item?.results
        };
      });

      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LIST_SUCCESS,
        payload: {
          data: listData,
          unModifiedData: data.data,
          paginatedElements: listData.length,
          units: data.units,
          permissions: data?.permissions
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      if (error?.status === 403) {
        closeDialog?.();
      }
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};

export const createHydraulics = (body, jobId, wellsInfoId, setId, wellsPage) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  const { name, run_details, notes, type } = body;
  delete body._id;
  delete body.isDirty;
  const input = {
    input: {
      name,
      run_details: run_details || {},
      wellsInfoId,
      jobId: (jobId || '-1'),
      notes,
      type,
      index: body?.index
    }
  };
  const query = wellsPage
    ? {
        wellsInfoId
      }
    : {
        jobId
      };
  return fetch
    .post('hydraulicsRuns/create', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      if (setId) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
          payload: {
            data: data.data._id,
          },
        });
      }
      dispatch(
        enqueueSnackbar(
          'Hydraulics Created Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    });
};
export const updateHydraulics = (body, wellsPage) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  delete body.isDirty;
  const input = {
    query: {
      _id: body?._id
    },
    input: {
      notes: body?.notes,
      jobId: (body?.jobId) || '-1',
      wellsInfoId: body?.wellsInfoId,
      name: body?.name,
      type: body?.type,
      index: body?.index
    }
  };
  const query = wellsPage
    ? {
        wellsInfoId: body?.wellsInfoId
      }
    : {
        jobId: body?.jobId
      };
  return fetch
    .put('hydraulicsRuns', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Updated Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    });
};
export const deleteHydraulics = (id, query) => (dispatch) => {
  const body = {
    query: {
      _id: id,
      ...query
    }
  };
  return fetch
    .deleteAction('hydraulicsRuns', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics deleted successfully',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    });
};

export const updateHydraulicsRunPosition = (body, listQuery) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  const input = {
    query: body
  };
  return fetch
    .put('hydraulicsRuns/switchPosition', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Run Position Updated Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(listQuery));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(listQuery));
    });
};

export const listHydraulicsDetails = (runsId, closeDialog) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
  });
  const body = {
    query: {
      runsId
    }
  };
  return fetch
    .post('hydraulicsRuns/details/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: hydraulicsDetailData, units } = data;
      const modifiedHydraulicsDetailData = modifyMetricData(hydraulicsDetailData, units.inputs, currentUnit);
      if (modifiedHydraulicsDetailData[0]?.cuttings_data) {
        const cuttingData = modifyMetricData(
          [modifiedHydraulicsDetailData[0].cuttings_data],
          units.inputs.cuttings_data,
          currentUnit
        );
        modifiedHydraulicsDetailData[0].cuttings_data = cuttingData[0];
      }
      if (modifiedHydraulicsDetailData[0]?.well_init) {
        const modifiedWellInitPipe = modifyMetricData(modifiedHydraulicsDetailData[0]?.well_init?.pipe, units.inputs?.well_init, currentUnit);
        const modifiedWellInitAnnular = modifyMetricData(modifiedHydraulicsDetailData[0]?.well_init?.annular, units.inputs?.well_init, currentUnit);
        modifiedHydraulicsDetailData[0].well_init = {
          pipe: modifiedWellInitPipe,
          annular: modifiedWellInitAnnular
        };
      }
      if (modifiedHydraulicsDetailData[0]?.target_data) {
        const targetData = modifyMetricData(
          [modifiedHydraulicsDetailData[0].target_data],
          units.inputs.target_data,
          currentUnit
        );
        const targetRunsECDSpan = modifiedHydraulicsDetailData[0].target_data?.ecd_span?.length
          ? modifyMetricData(
            modifiedHydraulicsDetailData[0].target_data?.ecd_span,
            units.inputs.ecd_span,
            currentUnit
          )
          : [];
        const targetControlsTable = modifiedHydraulicsDetailData[0].target_data?.controls_table?.length
          ? modifyMetricDataByKey(
            modifiedHydraulicsDetailData[0].target_data?.controls_table,
            units.inputs.target_data?.controls_table,
            currentUnit,
            'parameter'
          )
          : [];
        targetData[0].ecd_span = targetRunsECDSpan;
        targetData[0].controls_table = targetControlsTable;
        if (!targetData[0]?.anchor_point) {
          targetData[0].anchor_point = 'None';
          targetData[0].target_ecd = null;
        }
        modifiedHydraulicsDetailData[0].target_data = targetData[0];
      }
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SUCCESS,
        payload: {
          ...data,
          unModifiedData: data.data,
          data: modifiedHydraulicsDetailData,
          paginatedElements: modifiedHydraulicsDetailData.length,
          units: data.units
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      if (error?.status === 403) {
        closeDialog?.();
      }
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};

export const createHydraulicsDetails = (query, body) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
  });
  delete body._id;
  const input = {
    query,
    input: body
  };
  return fetch
    .post('hydraulicsRuns/details/create', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Details Created Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulicsDetails(query?.runsId));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulicsDetails(query?.runsId));
    });
};
export const updateHydraulicsDetails = (runsId, body, hydraulicsBody) => (dispatch) => {
  delete body?._id;
  const { query, isHydraulicsResults } = hydraulicsBody;
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
  });
  const input = {
    query: {
      runsId,
      type: query?.type,
      wellsInfoId: query?.wellsInfoId,
    },
    input: body
  };
  return fetch
    .put('hydraulicsRuns/details', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Details Updated Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulicsDetails(runsId));
      if (isHydraulicsResults) {
        dispatch(listHydraulics(query));
      }
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulicsDetails(runsId));
      if (isHydraulicsResults) {
        dispatch(listHydraulics(query));
      }
    });
};
export const deleteHydraulicsDetails = (runsId) => (dispatch) => {
  const input = {
    query: {
      runsId
    },
  };
  return fetch
    .deleteAction('hydraulicsRuns/details', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Details Deleted Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulicsDetails(runsId));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
    });
};
export const runHydraulics = (body, disableLoader, onClose) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
  });
  const { runId, query } = body;
  const input = {
    input: {
      url: `/runs/${runId}`,
      method: 'POST',
    }
  };
  return fetch
    .post('premiumHydraulics/proxy', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Hydraulics Simulation Completed Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      disableLoader();
      onClose();
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SET_FALSE,
      });
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      disableLoader();
      const errorMessage = await parseError(error);
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SET_FALSE,
      });
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    });
};

export const fetchHydraulicsDrillstringDrillFluid = (wellsInfoId) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DRILL_DETAILS_LOADING,
  });
  const body = {
    query: {
      wellsInfoId
    }
  };
  return fetch
    .post('hydraulicsRuns/selections/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { result: drillData, units } = data;
      const modifiedDrillFluidData = modifyMetricData(drillData.drillFluids, units, currentUnit);
      const modifiedDrillStringData = modifyMetricData(drillData.drillStrings, units, currentUnit);

      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DRILL_DETAILS_SUCCESS,
        payload: {
          data: {
            drillFluids: modifiedDrillFluidData,
            drillStrings: modifiedDrillStringData
          },
          units
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DRILL_DETAILS_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};

export const listMudPumps = (wellsInfoId) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_MUD_PUMPS_LIST_LOADING,
  });
  const body = {
    query: {
      wellsInfoId
    }
  };
  return fetch
    .post('hydraulicsMudPumps/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: mudPumpData, units } = data;
      const modifiedMudPumpData = modifyMetricData(mudPumpData, units, currentUnit);
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_MUD_PUMPS_LIST_SUCCESS,
        payload: {
          data: modifiedMudPumpData,
          paginatedElements: modifiedMudPumpData.length,
          units: data.units
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_MUD_PUMPS_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};
export const createMudPumps = (body, wellsInfoId, jobId) => (dispatch) => {
  delete body.isNewRow;
  delete body._id;
  const input = {
    input: {
      ...body,
      wellsInfoId,
      jobId
    }
  };
  return fetch
    .post('hydraulicsMudPumps/create', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Mud Pump Created Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(wellsInfoId));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(wellsInfoId));
    });
};
export const updateMudPumps = (body, payload) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_MUD_PUMPS_LIST_LOADING
  });
  const input = {
    query: {
      _id: body?._id
    },
    input: {
      ...payload,
      wellsInfoId: body.wellsInfoId,
      jobId: body.jobId,
      spm: Number(body.spm)
    }
  };
  return fetch
    .put('hydraulicsMudPumps', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Mud Pump Updated Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(body?.wellsInfoId));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(body?.wellsInfoId));
    });
};
export const deleteMudPumps = (_id, wellsInfoId) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_MUD_PUMPS_LIST_LOADING
  });
  const body = {
    query: {
      _id,
    }
  };
  return fetch
    .deleteAction('hydraulicsMudPumps', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Mud Pump deleted successfully',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(wellsInfoId));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listMudPumps(wellsInfoId));
    });
};

export const downloadRunInputs = (payload) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_DOWNLOAD_MODEL_LOADING,
  });
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  const body = {
    query: {
      runsId: payload.runsId,
      wellsInfoId: payload.wellsInfoId
    }
  };
  return fetch
    .downloadPost('hydraulicsRuns/inputs/download', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const fileName = `${payload.fileName}.json`;
      // Create an anchor element and set the download attribute
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      // Append the link to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DOWNLOAD_MODEL_IDLE,
      });
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LOADING_FALSE,
      });
      dispatch(
        enqueueSnackbar(
          ' Run model input downloaded successfully.',
          'success',
          new Date().getTime() + Math.random()
        )
      );
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_DOWNLOAD_MODEL_IDLE,
      });
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LOADING_FALSE,
      });
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
    });
};
export const downloadParametricPDF = (body, printPreview, disableLoader) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_PARAMETRIC_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
    type: body?.type,
    axis_range: {
      x: body.xAxis,
      y: body.yAxis
    }
  };
  try {
    const result = await fetch.downloadPost('reports/parametric', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-Parametric-Analysis-Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_PARAMETRIC_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_PARAMETRIC_PDF_IDLE,
    });
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_PARAMETRIC_PDF_ERROR,
    });
    disableLoader();
  }
};

export const downloadECDESDChartPDF = (body, printPreview, disableLoader) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_ECD_ESD_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
    type: body?.type,
    axis_range: {
      x: body.xAxis,
      y: body.yAxis
    }
  };
  try {
    const result = await fetch.downloadPost('reports/ecd', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-ECD_Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_ECD_ESD_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
      disableLoader();
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_ECD_ESD_PDF_IDLE,
    });
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_ECD_ESD_PDF_ERROR,
    });
    disableLoader();
  }
};
export const downloadRampSchedulePDF = (body, printPreview, disableLoader) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_RAMP_SCHEDULE_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
  };
  try {
    const result = await fetch.downloadPost('reports/rampSchedule', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-Ramp_Schedule_Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_RAMP_SCHEDULE_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_RAMP_SCHEDULE_PDF_IDLE,
    });
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_RAMP_SCHEDULE_PDF_ERROR,
    });
    disableLoader();
  }
};
export const downloadRolloverPDF = (body, printPreview, disableLoader) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_ROLLOVER_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
    type: body?.type,
    depth: body?.depth
  };
  try {
    const result = await fetch.downloadPost('reports/rollover', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-Rollover_Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_ROLLOVER_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_ROLLOVER_PDF_IDLE,
    });
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_ROLLOVER_PDF_ERROR,
    });
    disableLoader();
  }
};
export const getDrillFluidDetails = (drillId) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.WELLS_DRILL_FLUID_PROPERTY_LIST_LOADING,
  });
  const body = {
    query: {
      drillId
    }
  };
  return fetch
    .post('wells/entity/drillFluid/properties/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: drillFluidDetailsData, units } = data;
      const modifiedDrillFluidDetailsData = modifyMetricData(drillFluidDetailsData, units, currentUnit);
      dispatch({
        type: REDUX_ACTIONS.WELLS_DRILL_FLUID_PROPERTY_LIST_SUCCESS,
        payload: {
          data: modifiedDrillFluidDetailsData,
          paginatedElements: modifiedDrillFluidDetailsData.length,
          units: data.units
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.WELLS_DRILL_FLUID_PROPERTY_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};
export const downloadWellsRunsPDF = (body, printPreview, disableLoader, wellsPage) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_WELLS_RUN_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
  };
  try {
    const result = await fetch.downloadPost('reports/runs', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-Wells_Runs_Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_WELLS_RUN_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_WELLS_RUN_PDF_IDLE,
    });
    const query = wellsPage
      ? {
          wellsInfoId: body?.wellsInfoId
        }
      : {
          jobId: body?.jobId
        };
    dispatch(listHydraulics(query));
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_WELLS_RUN_PDF_ERROR,
    });
    disableLoader();
  }
};

export const copyRun = (body, onClose, copyAgainModal) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  if (onClose) {
    dispatch({
      type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_LOADING,
    });
  }
  const { runId, query, submitRunType } = body;
  const input = {
    query: {
      runId,
    },
    inputs: {
      type: submitRunType
    }
  };
  return fetch
    .post('hydraulicsRuns/copy', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Run Copied Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      if (onClose) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SET_FALSE,
        });
        onClose();
      }
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      if (onClose) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_DETAILS_LIST_SET_FALSE,
        });
        onClose();
      }
      if (error.status === 409 && copyAgainModal) {
        copyAgainModal();
      }
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LOADING_FALSE,
      });
    });
};
export const downloadDisplacementPDF = (body, printPreview, disableLoader) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.DOWNLOAD_DISPLACEMENT_PDF_LOADING,
  });
  const payload = {
    inputs: body?.inputs,
    jobId: body?.jobId,
    runId: body?.runId,
    wellsInfoId: body?.wellsInfoId,
    type: body?.type,
    depth: body?.depth
  };
  try {
    const result = await fetch.downloadPost('reports/displacement', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-Displacement_Report.pdf`;
    if (printPreview) {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      dispatch({
        type: REDUX_ACTIONS.DOWNLOAD_DISPLACEMENT_PDF_IDLE,
      });
      disableLoader();
      return;
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_DISPLACEMENT_PDF_IDLE,
    });
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    dispatch({
      type: REDUX_ACTIONS.DOWNLOAD_DISPLACEMENT_PDF_ERROR,
    });
    disableLoader();
  }
};
const downloadFileDirectly = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const listHydraulicsRunsResults = (query) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_RUN_RESULT_LIST_LOADING,
  });
  const body = {
    query
  };
  return fetch
    .post('hydraulicsRuns/results/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: hydraulicsRunResultsData, units } = data;
      const holeDepthUnit = units?.results?.depths_summary?.hole_depth;
      const {
        summary,
        depth_points,
        pump_ramps,
        pwd,
        depths_summary,
        inputs
      } = hydraulicsRunResultsData.results ?? {};

      const { casing_shoe, hole_depth, landing_point, ...otherDepthSummary } = depths_summary || {};
      const secondaryFieldsSet = new Set();
      const modifiedCasingShoe = modifyMetricData([casing_shoe], units?.results?.depths_summary?.casing_shoe, currentUnit)[0];
      const modifiedHoleDepth = modifyMetricData([hole_depth], units?.results?.depths_summary?.hole_depth, currentUnit)[0];
      const modifiedLandingPoint = modifyMetricData([landing_point], units?.results?.depths_summary?.landing_point, currentUnit)[0];
      const { target_data } = inputs;

      // Dynamically modify other keys
      const modifiedOtherDepths = {};
      for (const [key, value] of Object.entries(otherDepthSummary)) {
        const modifiedValue = modifyMetricData([value], units?.results?.depths_summary?.hole_depth, currentUnit)[0];
        modifiedOtherDepths[key] = modifiedValue;
        secondaryFieldsSet.add(key);
        if (!units.results?.depths_summary[key]) {
          units.results.depths_summary[key] = holeDepthUnit;
        }
      }

      const modifiedSummary = modifyMetricData(
        [summary],
        units?.results?.summary,
        currentUnit
      )[0];

      const modifiedDepthPoints = modifyMetricData(
        depth_points,
        units?.results?.depth_points,
        currentUnit
      );

      const modifiedInputs = modifyMetricData(
        [inputs],
        units?.inputs,
        currentUnit
      )[0];

      const modifiedTargetData = modifyMetricData(
        [target_data],
        units?.inputs?.target_data,
        currentUnit
      )[0];

      const modifiedPumpRamps = modifyMetricData(
        pump_ramps,
        units?.results?.pump_ramps,
        currentUnit
      );

      const modifiedPWD =
          Array.isArray(pwd) && pwd.length > 0
            ? modifyMetricData(pwd, units?.results?.pwd, currentUnit)
            : pwd;

      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_RUN_RESULT_LIST_SUCCESS,
        payload: {
          data: {
            results: {
              ...(hydraulicsRunResultsData.results),
              inputs: {
                ...modifiedInputs,
                target_data: modifiedTargetData
              },
              pump_ramps: modifiedPumpRamps,
              summary: modifiedSummary,
              depth_points: modifiedDepthPoints,
              pwd: modifiedPWD,
              depths_summary: {
                casing_shoe: modifiedCasingShoe,
                hole_depth: modifiedHoleDepth,
                landing_point: modifiedLandingPoint,
                ...modifiedOtherDepths
              }
            },
          },
          units
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_RUN_RESULT_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};

export const downloadDataReport = (body, disableLoader) => async (dispatch) => {
  const payload = {
    inputs: {
      type: body?.type,
      runId: body?.runId,
      wellsInfoId: body?.wellsInfoId,
    }
  };
  try {
    const result = await fetch.downloadPost('dataReports/fetch', payload);
    if (!result.ok) {
      throw result;
    }
    const blob = await result.blob();
    const fileName = `${body?.rigName}-${body?.wellName}-${body?.runName}-Data.xlsx`;
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'Excel Workbook',
              accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              },
            },
          ],
        });
        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
        dispatch(
          enqueueSnackbar(
            'File saved successfully!',
            'success',
            new Date().getTime() + Math.random()
          )
        );
        disableLoader();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          dispatch(
            enqueueSnackbar(
              'Download cancelled',
              'warning',
              new Date().getTime() + Math.random()
            )
          );
          disableLoader();
        } else {
          downloadFileDirectly(blob, fileName);
          disableLoader();
        }
      }
    } else {
      downloadFileDirectly(blob, fileName);
    }
  } catch (error) {
    const errorMessage = await parseError(error);
    dispatch(
      enqueueSnackbar(
        errorMessage,
        'error',
        new Date().getTime() + Math.random()
      )
    );
    disableLoader();
  }
};
